package com.karyadina.model

import kotlinx.serialization.Serializable

/**
 * Study session type
 */
enum class StudySessionType {
    LECTURE,
    SELF_STUDY,
    GROUP_STUDY,
    REVISION,
    PRACTICE,
    EXAM,
    FLASHCARD_REVIEW // New type for flashcard sessions
}

/**
 * Day of week
 */
enum class DayOfWeek {
    MONDAY,
    TUESDAY,
    WEDNESDAY,
    THURSDAY,
    FRIDAY,
    SATURDAY,
    SUNDAY
}

/**
 * Study session
 */
@Serializable
data class StudySession(
    val id: String,
    val subject: String,
    val topic: String = "",
    val sessionType: String = StudySessionType.SELF_STUDY.name,
    val startTime: Long, // timestamp
    val endTime: Long, // timestamp
    val durationMinutes: Int,
    val completed: Boolean = false,
    val notes: String = "",
    val location: String = "",
    val tags: List<String> = emptyList(),
    val linkedTasks: List<String> = emptyList(), // task IDs
    // Real-time tracking fields
    val targetDurationMinutes: Int = durationMinutes, // Original planned duration
    val actualDurationMinutes: Int = 0, // Time actually spent
    val isTracking: Boolean = false, // Currently being tracked
    val trackingStartTime: Long? = null, // When tracking started
    val pausedDurationMinutes: Int = 0, // Total paused time
    // Flashcard integration
    val linkedFlashcardSubjectId: String? = null, // Link to flashcard subject
    val linkedFlashcardUnitId: String? = null, // Link to specific unit/topic
    val flashcardStats: FlashcardSessionStats? = null, // Stats from completed flashcard review
    val createdAt: Long = 0,
    val updatedAt: Long = 0
)

@Serializable
data class FlashcardSessionStats(
    val cardsReviewed: Int = 0,
    val correctAnswers: Int = 0,
    val incorrectAnswers: Int = 0,
    val sessionDurationMinutes: Int = 0
)

/**
 * Weekly study schedule
 */
@Serializable
data class StudySchedule(
    val id: String,
    val weekStartDate: Long,
    val sessions: List<StudySession> = emptyList(),
    val totalPlannedHours: Float = 0f,
    val totalCompletedHours: Float = 0f
)

/**
 * Subject
 */
@Serializable
data class Subject(
    val id: String,
    val name: String,
    val code: String = "",
    val color: String = "#2196F3",
    val targetHoursPerWeek: Float = 0f,
    val notes: String = "",
    val examDate: Long? = null,
    val createdAt: Long = 0,
    val updatedAt: Long = 0
)

/**
 * Study goal
 */
@Serializable
data class StudyGoal(
    val id: String,
    val title: String,
    val description: String = "",
    val targetHours: Float,
    val currentHours: Float = 0f,
    val deadline: Long? = null,
    val subject: String? = null,
    val completed: Boolean = false
)

/**
 * Study statistics
 */
@Serializable
data class StudyStats(
    val totalHours: Float = 0f,
    val todayHours: Float = 0f,
    val weekHours: Float = 0f,
    val monthHours: Float = 0f,
    val averageDailyHours: Float = 0f,
    val mostStudiedSubject: String? = null,
    val currentStreak: Int = 0,
    val longestStreak: Int = 0
)
